import { motion } from 'framer-motion';
import { SERVICES } from '@/data/site';
import { SERVICES_IDS } from '@/constants/testIds';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1];

export default function ServicesGrid({ compact = false }) {
  const items = compact ? SERVICES.slice(0, 6) : SERVICES;
  return (
    <section data-testid={SERVICES_IDS.section} className="relative bg-white py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-14 md:mb-20">
          <div className="md:col-span-4">
            <div className="font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#1D4ED8] flex items-center gap-3">
              <span className="w-8 h-px bg-[#1D4ED8]" /> What we build
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display uppercase text-[#0A0A0A] text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              A full studio, one crew,<br />zero handoffs.
            </h2>
            <p className="mt-6 text-[#0A0A0A]/70 max-w-2xl">
              Every discipline in-house or on our short list. Design-build workflows so nothing gets lost between the drawing and the pour.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10">
          {items.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={SERVICES_IDS.card(s.id)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.06 }}
              className="group relative border-r border-b border-black/10 p-8 md:p-10 hover:bg-black/[0.02] transition-colors"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono-label text-[11px] tracking-[0.24em] text-[#0A0A0A]/60 group-hover:text-[#1D4ED8] transition-colors">
                  {s.num} / SVC
                </span>
                <ArrowUpRight className="w-5 h-5 text-[#0A0A0A]/30 group-hover:text-[#1D4ED8] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
              </div>
              <h3 className="font-display uppercase text-[#0A0A0A] text-2xl md:text-3xl leading-tight tracking-tight mb-3">
                {s.title}
              </h3>
              <p className="text-[#0A0A0A]/65 text-sm md:text-base leading-relaxed">{s.blurb}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span key={t} className="font-mono-label text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border border-black/10 text-[#0A0A0A]/70">
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {compact && (
          <div className="mt-12 flex justify-end">
            <Link
              to="/services"
              className="inline-flex items-center gap-3 border border-black/15 text-[#0A0A0A] px-6 py-3 font-mono-label text-[11px] tracking-[0.24em] uppercase hover:bg-black/5 transition-colors"
            >
              All services →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
